import type Icon from 'ol/style/Icon';
import { canvasIcon, HALO } from './symbolCanvas';

/**
 * «Motor-bomba de riego» (V1.8, compact): a small cartographic drawing of an agricultural pumping set — diesel engine
 * block with radiator, valve cover and exhaust, coupled to a metallic centrifugal pump with its discharge and suction
 * pipes, on a skid. No plate or box: a thin light halo around the silhouette keeps it legible on satellite imagery.
 * At 100 % it covers 32 × 20 px (V1.5–V1.7: 52.8 × 36 px over a white plate). Generic: no brand marks.
 */

/** Size on the map at 100 %, in CSS pixels at render scale 1. */
export const PUMP_SYMBOL_SIZE = { width: 32, height: 20 } as const;

const ENGINE_DARK = '#1d3f17';
const METAL_DARK = '#3e464d';
const PIPE = '#1565c0';

/** Draws the symbol on a 32 × 20 logical grid (`u` converts logical units to canvas pixels). */
export function drawPumpUnit(context: CanvasRenderingContext2D, u: (value: number) => number, engineColor: string, proposed: boolean): void {
  const box = (x: number, y: number, width: number, height: number, radius: number, fill: string, stroke?: string, lineWidth = 0.8): void => {
    context.beginPath(); context.roundRect(u(x), u(y), u(width), u(height), u(radius));
    context.fillStyle = fill; context.fill();
    if (stroke) { context.strokeStyle = stroke; context.lineWidth = u(lineWidth); context.stroke(); }
  };
  const line = (points: readonly (readonly [number, number])[], color: string, width: number, cap: CanvasLineCap = 'butt'): void => {
    context.beginPath(); points.forEach(([x, y], index) => (index ? context.lineTo(u(x), u(y)) : context.moveTo(u(x), u(y))));
    context.strokeStyle = color; context.lineWidth = u(width); context.lineCap = cap; context.lineJoin = 'round'; context.stroke();
  };
  // Halo: the silhouette (engine, pump, skid, pipes) stroked in a light colour, under the drawing.
  context.save();
  context.strokeStyle = HALO; context.lineJoin = 'round'; context.lineCap = 'round'; context.lineWidth = u(2.2);
  context.beginPath(); context.roundRect(u(2), u(3.6), u(13), u(12), u(1.4)); context.stroke();
  context.beginPath(); context.arc(u(23.4), u(10.6), u(5.1), 0, Math.PI * 2); context.stroke();
  context.beginPath(); context.roundRect(u(2), u(15.4), u(28), u(2.4), u(0.8)); context.stroke();
  line([[23.4, 5.5], [23.4, 2.4], [30.4, 2.4]], HALO, 4.2, 'round'); line([[28.4, 10.6], [31, 10.6]], HALO, 4.2, 'round');
  context.restore();
  // Skid with feet.
  box(2, 15.4, 28, 2.4, 0.8, '#33373b'); box(4, 17.6, 2.2, 1.6, 0.4, '#33373b'); box(25.6, 17.6, 2.2, 1.6, 0.4, '#33373b');
  // Engine block, valve cover, radiator grille, exhaust stack.
  box(2, 6.4, 13, 9.2, 1.4, engineColor, ENGINE_DARK, 0.7);
  box(4.4, 3.8, 8.4, 3, 1, engineColor, ENGINE_DARK, 0.6);
  box(2, 7.2, 2.8, 7.6, 0.6, '#24272b');
  [9.2, 11.2, 13.2].forEach((y) => line([[2.5, y], [4.3, y]], '#a7b0b8', 0.5));
  box(10.2, 1, 1.6, 3, 0.3, '#24272b');
  // Coupling to the pump.
  box(15, 9.2, 2.6, 3, 0.5, '#8a939c', METAL_DARK, 0.4);
  // Centrifugal pump: metallic volute and eye.
  context.beginPath(); context.arc(u(23.4), u(10.6), u(5.1), 0, Math.PI * 2);
  const metal = context.createLinearGradient(u(18.3), u(5.5), u(28.5), u(15.7)); metal.addColorStop(0, '#e3e7ea'); metal.addColorStop(1, '#7f8992');
  context.fillStyle = metal; context.fill(); context.strokeStyle = METAL_DARK; context.lineWidth = u(0.7); context.stroke();
  context.beginPath(); context.arc(u(23.4), u(10.6), u(1.7), 0, Math.PI * 2); context.fillStyle = '#56616a'; context.fill();
  // Discharge up and out (with flange) and suction on the side.
  line([[23.4, 5.5], [23.4, 2.4], [29.6, 2.4]], PIPE, 2);
  box(29.2, 0.9, 1.6, 3, 0.3, '#0d3c78');
  line([[28.5, 10.6], [31, 10.6]], PIPE, 2);
  // Proposed equipment: thin dashed outline instead of a plate.
  if (proposed) {
    context.setLineDash([u(1.6), u(1.2)]); context.strokeStyle = '#ffffff'; context.lineWidth = u(0.7);
    context.beginPath(); context.roundRect(u(0.5), u(0.4), u(31), u(19.2), u(2.4)); context.stroke(); context.setLineDash([]);
  }
}

/** Icon at `size` (symbol scale × render scale) and opacity; null where no 2D canvas exists (callers fall back). */
export function pumpIcon(engineColor: string, proposed: boolean, size = 1, opacity = 1): Icon | null {
  return canvasIcon(`pump|${engineColor}|${proposed}`, PUMP_SYMBOL_SIZE.width, PUMP_SYMBOL_SIZE.height, size, opacity, (context, u) => drawPumpUnit(context, u, engineColor, proposed));
}
